/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from "react";

import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Select } from "../../../../../../../../components/Form";
import AutoSuggest from "../../../../../../../../components/Form/AutoSuggest";
import Input from "../../../../../../../../components/Form/Input";
import InputRange from "../../../../../../../../components/Form/InputRange";
import RadioButton from "../../../../../../../../components/Form/RadioGroup";
import Modal, { StatusButtonProps } from "../../../../../../../../components/Generic/Modal";
import { useAppState } from "../../../../../../../../context/StateContext";
import { Item } from "../../../../../../../../models/item";
import { Room } from "../../../../../../../../models/room";
import { ServiceBooking as PackingMaterial } from "../../../../../../../../models/service.booking";
import MaterialsService from "../../../../../../../../services/materials.service";
import RoomService from "../../../../../../../../services/room.service";
import { AvailableIn, Status } from "../../../../../../../../util/enums";
import { getMeasurement, getWeightMeasurement } from "../../../../../../../../util/getMeasurement";
import useStyles from "./styles";

interface Props {
  itemToUpdate?: Item;
  errors?: boolean;
  open: boolean;
  isLoading: boolean;
  isView: boolean;
  setIsOnViewMode: () => void;
  closeModal: () => void;
  submit: (item: Item) => void;
}

export const ItemModal = ({
  itemToUpdate,
  open,
  errors,
  isLoading,
  isView,
  closeModal,
  submit,
}: Props) => {
  const classes = useStyles();
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const [itemName, setItemName] = useState<string>("");
  const [itemCode, setItemCode] = useState<string>("");
  const [availableIn, setAvailableIn] = useState<string>(AvailableIn.BOTH);
  const [itemVolume, setItemVolume] = useState<number>(0);
  const [itemWeight, setItemWeight] = useState<number>(0);
  const [itemLocation, setItemLocation] = useState<Room[]>([]);
  const [itemMaterials, setItemMaterials] = useState<PackingMaterial[]>([]);
  // TODO Felipe check if use relation between item and item_type (packaging type)
  // const [itemPackage, setItemPackage] = useState<ItemTypes[]>([]);
  const [itemIsHardwareBox, setItemIsHardwareBox] = useState<boolean>(false);
  const [itemHasPhoto, setItemHasPhoto] = useState<boolean>(false);
  const [itemHasBrand, setItemHasBrand] = useState<boolean>(false);
  const [itemHasChecklist, setItemHasChecklist] = useState<boolean>(false);
  const [itemHighRisk, setItemHighRisk] = useState<boolean>(false);
  const [itemProGear, setItemProGear] = useState<boolean>(false);
  const [itemIsVehicle, setItemIsVehicle] = useState<boolean>(false);
  const [itemVehicleType, setItemVehicleType] = useState<string>("");
  const [itemIsTv, setItemIsTv] = useState<boolean>(false);
  const [status, setStatus] = useState<string>(Status.INACTIVE);

  const [locationName] = useState<string>("");
  const [yupErrors, setYupErrors] = useState<yup.ValidationError[]>([]);
  const [locationList, setLocationList] = useState<Room[]>([]);
  const [materialList, setMaterialList] = useState<PackingMaterial[]>([]);
  // TODO Felipe check if use relation between item and item_type (packaging type)
  // const [packageList, setPackageList] = useState<ItemTypes[]>([]);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);

  const getErrorByField = (field: string) =>
    yupErrors?.find((error) => error.path === field)?.message;

  useQuery(
    ["room.list"],
    () =>
      RoomService.getAll({
        status: Status.ACTIVE,
        jobId: "",
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) setLocationList(response.data);
      },
    }
  );

  // TODO Felipe check if use relation between item and item_type (packaging type)
  // useQuery(
  //   ["package.list"],
  //   () =>
  //     ItemTypesService.getAll({
  //       companyId: currentUser?.companyId,
  //       status: Status.ACTIVE,
  //       sortBy: JSON.stringify([["name", "asc"]])
  //     }),
  //   {
  //     enabled: !!currentUser?.id,
  //     refetchOnWindowFocus: false,
  //     onSuccess: response => {
  //       if (response && response.data && response.data.length > 0)
  //         setPackageList(response.data);
  //     }
  //   }
  // );

  useQuery(
    ["materials.list"],
    () =>
      MaterialsService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) setMaterialList(response.data);
      },
    }
  );

  const schema = yup.object().shape({
    itemName: yup.string().required(translate("thisFieldIsRequired")),
    itemCode: yup.string().required(translate("thisFieldIsRequired")),
    itemVolume: yup
      .number()
      .test("empty-check", translate("thisFieldIsRequired"), (itemVolume) =>
        itemVolume ? itemVolume > 0 : false
      ),
    itemWeight: yup.number(),
    isVehicle: yup.boolean(),
    vehicleType: yup.string().when("isVehicle", {
      is: true,
      then: (s) =>
        s.required(translate("thisFieldIsRequired")).min(1, translate("thisFieldIsRequired")),
      otherwise: (s) => s.notRequired(),
    }),
  });

  const handleValidateSuccess = (item?: Item): boolean => {
    schema.validateSync(
      {
        itemName: item?.name,
        itemCode: item?.code,
        itemVolume: item?.volume,
        itemWeight: item?.weight,
        isVehicle: item?.isVehicle,
        vehicleType: item?.isVehicle ? item?.vehicleType : undefined,
      },
      { abortEarly: false }
    );
    setYupErrors([]);

    return true;
  };

  const handleValidateError = (error: any): boolean => {
    if (error instanceof yup.ValidationError) setYupErrors(error?.inner);

    return false;
  };

  const isValid = (item: Item) => {
    try {
      return handleValidateSuccess(item);
    } catch (error) {
      return handleValidateError(error);
    }
  };

  const clearFields = () => {
    setItemName("");
    setItemCode("");
    setAvailableIn(AvailableIn.BOTH);
    setItemVolume(0);
    setItemWeight(0);
    setItemLocation([]);
    setItemMaterials([]);
    // TODO Felipe check if use relation between item and item_type (packaging type)
    // setItemPackage([]);
    setItemIsHardwareBox(false);
    setItemHasPhoto(false);
    setItemHasBrand(false);
    setItemHasChecklist(false);
    setItemHighRisk(false);
    setItemProGear(false);
    setItemIsVehicle(false);
    setItemVehicleType("");
    setItemIsTv(false);
  };

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    const object = {
      name: itemName,
      volume: Number(itemVolume),
      rooms: itemLocation,
      code: itemCode,
      weight: Number(itemWeight || 0),
      packingMaterials: itemMaterials,
      // TODO Felipe check if use relation between item and item_type (packaging type)
      // packagings: itemPackage,
      isHardwareBox: itemIsHardwareBox,
      mandatoryPhotos: itemHasPhoto,
      mandatoryBrand: itemHasBrand,
      mandatoryME: itemHasChecklist,
      highRisk: itemHighRisk,
      isProGear: itemProGear,
      isVehicle: itemIsVehicle,
      vehicleType: itemIsVehicle ? itemVehicleType : undefined,
      isTv: itemIsTv,
      availableIn,
      status,
    } as Item;

    if (isValid(object)) submit(object);
  };

  useEffect(() => {
    if (itemToUpdate) {
      setItemName(itemToUpdate.name);
      setItemWeight(itemToUpdate.weight || 0);
      setItemCode(itemToUpdate.code || "");
      setAvailableIn(itemToUpdate.availableIn || "");
      setItemVolume(itemToUpdate.volume);
      setItemLocation(itemToUpdate.rooms || ([] as Room[]));
      setItemIsHardwareBox(itemToUpdate.isHardwareBox);
      setItemHasPhoto(itemToUpdate.mandatoryPhotos);
      setItemHasBrand(itemToUpdate.mandatoryBrand);
      setItemHasChecklist(itemToUpdate.mandatoryME);
      setItemMaterials(itemToUpdate.packingMaterials || []);
      setItemHighRisk(itemToUpdate.highRisk);
      setItemProGear(itemToUpdate.isProGear);
      setItemIsVehicle(itemToUpdate.isVehicle || false);
      setItemVehicleType(itemToUpdate.vehicleType || "");
      setItemIsTv(Boolean(itemToUpdate.isTv));
      // TODO Felipe check if use relation between item and item_type (packaging type)
      // setItemPackage(itemToUpdate.packagings);
      setStatus(itemToUpdate.status || "");
    }
  }, [itemToUpdate]);

  useEffect(() => {
    if (errors !== undefined && !errors) {
      clearFields();
      closeModal();
    }
  }, [errors]);

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      isValid({
        name: itemName,
        volume: Number(itemVolume),
        code: itemCode,
        weight: Number(itemWeight || 0),
        isVehicle: itemIsVehicle,
        vehicleType: itemIsVehicle ? itemVehicleType : undefined,
      } as Item);
    }
  }, [
    itemName,
    itemVolume,
    itemCode,
    itemWeight,
    itemIsVehicle,
    itemVehicleType,
    hasFormBeenSubmitted,
  ]);

  const statusButtonProps: StatusButtonProps = {
    status: status || Status.INACTIVE,
    action: (status) => setStatus(status),
  };

  return (
    <Modal
      data-cy="itemModal"
      open={open}
      handleClose={() => {
        clearFields();
        closeModal();
      }}
      title={
        itemToUpdate
          ? isView
            ? translate("viewItem")
            : translate("editItem")
          : translate("createNewItem")
      }
      dataCyTitle="itemModalTitle"
      showActionButtons
      actionButtonProps={{
        textSave: itemToUpdate ? translate("update") : translate("create"),
        dataCySave: "submitButton",
        loading: isLoading,
        saveDisabled: isLoading,
        onSave: isView ? undefined : () => handleSubmit(),
        onCancel: () => {
          clearFields();
          closeModal();
        },
      }}
      labelStatus={translate("status")}
      showButtonStatus={!isView}
      statusButtonProps={statusButtonProps}
    >
      <section className={classes.modalSection}>
        <Grid container direction="row" alignItems="flex-start" spacing={3}>
          <Grid item md={6} lg={6}>
            <Input
              disabled={isView}
              name="itemCode"
              label={translate("code")}
              placeholder={translate("code")}
              value={itemCode}
              error={!!getErrorByField("itemCode")}
              errorMessage={getErrorByField("itemCode")}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setItemCode(e.target.value)}
              required
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <Input
              disabled={isView}
              name="itemName"
              label={translate("name")}
              placeholder={translate("name")}
              value={itemName}
              error={!!getErrorByField("itemName")}
              errorMessage={getErrorByField("itemName")}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setItemName(e.target.value)}
              required
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <InputRange
              data-cy="itemVolume"
              disabled={isView}
              name="itemVolume"
              label={translate("volume")}
              minValue={0}
              measurementInputRange={getMeasurement()}
              value={itemVolume}
              onIncrease={(value) => setItemVolume(value)}
              onDecrease={(value) => setItemVolume(value)}
              change={(value) => {
                const nValue = Number(value);

                if (!Number.isNaN(nValue)) setItemVolume(nValue);
              }}
              error={!!getErrorByField("itemVolume")}
              errorMessage={getErrorByField("itemVolume")}
              required
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <InputRange
              disabled={isView}
              name="itemWeight"
              label={translate("weight")}
              minValue={0}
              measurementInputRange={getWeightMeasurement()}
              value={itemWeight}
              onIncrease={(value) => setItemWeight(value)}
              onDecrease={(value) => setItemWeight(value)}
              change={(value) => {
                const nValue = Number(value);

                if (!Number.isNaN(nValue)) setItemWeight(nValue);
              }}
              error={!!getErrorByField("itemWeight")}
              errorMessage={getErrorByField("itemWeight")}
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <AutoSuggest
              disableCloseOnSelect
              disabled={isView}
              data-cy="autosuggestLocation"
              label={translate("location")}
              options={locationList}
              value={itemLocation}
              inputValue={locationName}
              getOptionLabel={(location: Room) => (location && location.name ? location.name : "")}
              getOptionSelected={(option: Room, value: Room) => option.id === value.id}
              multiple
              onChange={(tags: Room[]) => {
                setItemLocation(tags);
              }}
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <AutoSuggest
              disabled={isView}
              label={translate("materials")}
              value={itemMaterials}
              options={materialList}
              getOptionLabel={(material: PackingMaterial) => material?.name}
              getOptionSelected={(option: PackingMaterial, value: PackingMaterial) =>
                option.id === value.id
              }
              multiple
              onChange={(materials: PackingMaterial[]) => setItemMaterials(materials)}
            />
            {/* //TODO Felipe check if use relation between item and item_type (packaging type)
            <AutoSuggest
              disabled={isView}
              label={translate("label.item.packaging")}
              value={itemPackage}
              options={packageList}
              getOptionLabel={(material: ItemTypes) =>
                material && material.name ? material.name : ""
              }
              getOptionSelected={(option: ItemTypes, value: ItemTypes) =>
                option.id === value.id
              }
              multiple
              onChange={(packaging: ItemTypes[]) => setItemPackage(packaging)}
            /> */}
          </Grid>
        </Grid>
        <Grid container direction="row" alignItems="flex-start" spacing={3}>
          <Grid item md={6} lg={6}>
            <Select
              label={translate("availableIn")}
              value={availableIn}
              onChange={(event: SelectChangeEvent<any>) => setAvailableIn(event?.target?.value)}
              displayEmpty
              placeholder={translate("select")}
              data-cy="status"
            >
              <MenuItem value={AvailableIn.BOTH}>{translate("salesAndOPS")}</MenuItem>
              <MenuItem value={AvailableIn.OPS}>{translate("ops")}</MenuItem>
              <MenuItem value={AvailableIn.SALES}>{translate("sales")}</MenuItem>
            </Select>
          </Grid>
        </Grid>
        <Grid container direction="row" alignItems="center" spacing={3}>
          <Grid item md={6} lg={6}>
            <RadioButton
              disabled={isView}
              value={itemIsHardwareBox}
              label={translate("itemIsAHardwareBox")}
              options={[
                { label: translate("yes"), value: true },
                { label: translate("no"), value: false },
              ]}
              onChange={(e) =>
                e?.target?.value === "true"
                  ? setItemIsHardwareBox(true)
                  : setItemIsHardwareBox(false)
              }
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <RadioButton
              disabled={isView}
              value={itemHasPhoto}
              label={translate("photoIsMandatory")}
              options={[
                { label: translate("yes"), value: true },
                { label: translate("no"), value: false },
              ]}
              onChange={(event) =>
                event?.target?.value === "true" ? setItemHasPhoto(true) : setItemHasPhoto(false)
              }
            />
          </Grid>
        </Grid>
        <Grid container direction="row" alignItems="center" spacing={3}>
          <Grid item md={6} lg={6}>
            <RadioButton
              disabled={isView}
              value={itemHasBrand}
              label={translate("commentIsMandatory")}
              options={[
                { label: translate("yes"), value: true },
                { label: translate("no"), value: false },
              ]}
              onChange={(event) =>
                event?.target?.value === "true" ? setItemHasBrand(true) : setItemHasBrand(false)
              }
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <RadioButton
              disabled={isView}
              value={itemHasChecklist}
              label={translate("mEChecklistIsMandatory")}
              options={[
                { label: translate("yes"), value: true },
                { label: translate("no"), value: false },
              ]}
              onChange={(event) =>
                event?.target?.value === "true"
                  ? setItemHasChecklist(true)
                  : setItemHasChecklist(false)
              }
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <RadioButton
              disabled={isView}
              value={itemHighRisk}
              label={translate("itemIsHighRiskOrHighValue")}
              options={[
                { label: translate("yes"), value: true },
                { label: translate("no"), value: false },
              ]}
              onChange={(event) =>
                event?.target?.value === "true" ? setItemHighRisk(true) : setItemHighRisk(false)
              }
            />
          </Grid>
          {currentUser?.company?.isMilitary && (
            <Grid item md={6} lg={6}>
              <RadioButton
                disabled={isView}
                value={itemProGear}
                label={translate("itemIsProGear")}
                options={[
                  { label: translate("yes"), value: true },
                  { label: translate("no"), value: false },
                ]}
                onChange={(event) =>
                  event?.target?.value === "true" ? setItemProGear(true) : setItemProGear(false)
                }
              />
            </Grid>
          )}
          <Grid item md={6} lg={6}>
            <RadioButton
              disabled={isView}
              value={itemIsVehicle}
              label={translate("itemIsVehicle")}
              options={[
                { label: translate("yes"), value: true },
                { label: translate("no"), value: false },
              ]}
              onChange={(event) => {
                const val = event?.target?.value === "true";
                setItemIsVehicle(val);
                if (!val) setItemVehicleType("");
              }}
            />
          </Grid>
          {itemIsVehicle && (
            <Grid item md={6} lg={6}>
              <Select
                label={translate("vehicleType")}
                value={itemVehicleType}
                onChange={(event: SelectChangeEvent<any>) => setItemVehicleType(event?.target?.value)}
                displayEmpty
                placeholder={translate("select")}
                disabled={isView}
                error={!!getErrorByField("vehicleType")}
                errorMessage={getErrorByField("vehicleType")}
              >
                <MenuItem value="Motorcycle">{translate("motorcycle")}</MenuItem>
                <MenuItem value="Car">{translate("car")}</MenuItem>
                <MenuItem value="Boat">{translate("boat")}</MenuItem>
              </Select>
            </Grid>
          )}
          <Grid item md={6} lg={6}>
            <RadioButton
              disabled={isView}
              value={itemIsTv}
              label={translate("itemIsTv")}
              options={[
                { label: translate("yes"), value: true },
                { label: translate("no"), value: false },
              ]}
              onChange={(event) =>
                event?.target?.value === "true" ? setItemIsTv(true) : setItemIsTv(false)
              }
            />
          </Grid>
        </Grid>
      </section>
    </Modal>
  );
};
